/**
 * Shared shape of every single-player arcade game.
 *
 * A game is a fixed-timestep simulation (ARCADE_FPS steps per second) of
 * integer state, driven by a seeded PRNG and a log of input codes. The client
 * steps it in real time and renders it; the server replays the recorded log
 * with the same seed and must arrive at the same score. Anything a player
 * could edit (time, randomness, collisions) lives in the simulation, so a
 * forged score simply fails to replay.
 */

export const ARCADE_FPS = 60;

/** One recorded input: the simulation frame it was applied on, and its code. */
export type ArcadeInput = readonly [frame: number, code: number];

/** Cosmetic happenings of the last step (sounds, particles). Never affect the simulation. */
export interface ArcadeFx {
  kind: string;
  x: number;
  y: number;
  value?: number;
}

export interface ArcadeBaseState {
  frame: number;
  over: boolean;
  score: number;
  /** Filled by each step for the renderer; cleared at the start of the next. */
  fx: ArcadeFx[];
}

export interface ArcadeSimulation<S extends ArcadeBaseState> {
  /** Hard stop, in frames, even if the game has no other ending. */
  maxFrames: number;
  create(seed: number): S;
  /** Advance one frame, applying the codes recorded for this frame (in order). */
  step(state: S, codes: readonly number[]): void;
  validCode(code: number): boolean;
}

/** Integer clamp. */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Progress through the run in thousandths (0..1000), for difficulty ramps. */
export function progress1000(frame: number, total: number): number {
  return clamp(Math.floor((frame * 1000) / total), 0, 1000);
}
